import { AppError } from "../../shared/errors/AppError";

export type WebGL2RendererOptions = {
  alpha?: boolean;
  antialias?: boolean;
  preserveDrawingBuffer?: boolean;
};

export class WebGL2Renderer {
  readonly canvas: HTMLCanvasElement;
  readonly gl: WebGL2RenderingContext;
  private lost = false;

  constructor(canvas: HTMLCanvasElement, options: WebGL2RendererOptions = {}) {
    this.canvas = canvas;
    const gl = canvas.getContext("webgl2", {
      alpha: options.alpha ?? true,
      antialias: options.antialias ?? false,
      preserveDrawingBuffer: options.preserveDrawingBuffer ?? false,
      premultipliedAlpha: true,
      powerPreference: "high-performance",
    });

    if (!gl) throw new AppError("GPU_UNAVAILABLE", "WebGL2 is unavailable in this browser or device.");
    this.gl = gl;

    canvas.addEventListener("webglcontextlost", this.handleContextLost);
    canvas.addEventListener("webglcontextrestored", this.handleContextRestored);
  }

  private handleContextLost = (event: Event) => {
    event.preventDefault();
    this.lost = true;
  };

  private handleContextRestored = () => {
    this.lost = false;
  };

  resize(width: number, height: number) {
    const w = Math.max(1, Math.floor(width));
    const h = Math.max(1, Math.floor(height));
    if (this.canvas.width !== w) this.canvas.width = w;
    if (this.canvas.height !== h) this.canvas.height = h;
    if (!this.lost) this.gl.viewport(0, 0, w, h);
  }

  clear(red = 0, green = 0, blue = 0, alpha = 0) {
    if (this.lost) return;
    this.gl.clearColor(red, green, blue, alpha);
    this.gl.clear(this.gl.COLOR_BUFFER_BIT);
  }

  isContextLost() {
    return this.lost || this.gl.isContextLost();
  }

  dispose() {
    this.canvas.removeEventListener("webglcontextlost", this.handleContextLost);
    this.canvas.removeEventListener("webglcontextrestored", this.handleContextRestored);
    const extension = this.gl.getExtension("WEBGL_lose_context");
    extension?.loseContext();
  }
}
